import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import useResendOtp from 'features/otp/hooks/useResendOtp/useResendOtp';
import { sendOTPRequest } from 'features/v2a/receiver/helpers/Payments';
import { ChallengeData } from 'features/v2a/receiver/helpers/V2A.helpers';
import { useContext, useState } from 'react';
import { FeatureFlagKey } from 'services/statsig/types';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

export interface VerifyAttributeOtpRequest {
  code: string;
  challengeId: string;
}

export interface VerifyAttributeOtpResponse {
  challengeVerified: string;
}

const usePaymentOtp = (challengeData: ChallengeData) => {
  const postResendOTP = useResendOtp();

  const [showLoader, setShowLoader] = useState(false);
  const [challengeVerified, setChallengeVerified] = useState(false);
  const [otpError, setOtpError] = useState<string>('');
  const { getVaroSessionId, idToken } =
    useContext<AuthContextProps>(AuthContext);
  const authUrl = useAuthUrl(
    FeatureFlagKey.VERIFY_ATTRIBUTE_USE_K8S_AUTH_SERVICE,
  );

  const postSendOTPRequest = async (
    verifyOtpData: VerifyAttributeOtpRequest,
  ) => {
    const varoSessionId = await getVaroSessionId();
    return await sendOTPRequest(authUrl, varoSessionId, verifyOtpData);
  };

  const handleVerifyChallengeAttribute = async (code: string) => {
    const verifyOtpData = {
      challengeId: challengeData.challengeId,
      code,
    };

    try {
      setShowLoader(true);
      const response = await postSendOTPRequest(verifyOtpData);
      if ('challengeVerified' in response) {
        if (response?.challengeVerified === 'true') {
          setChallengeVerified(true);
          return;
        }
      } else if ('errorCode' in response) {
        if (response?.errorCode) {
          setOtpError(response.message);
        }
      }
    } catch (e) {
      logBreadcrumb({
        level: 'error',
        message: `Error in verifying OTP : ${e}`,
      });
    } finally {
      setShowLoader(false);
    }
  };

  const handleResendOTP = async () => {
    const varoSessionId = await getVaroSessionId();
    const headers = await getLambdaHeaders(idToken, varoSessionId);

    return await postResendOTP(
      {
        challengeId: challengeData.challengeId,
      },
      headers,
    );
  };

  return {
    showLoader,
    challengeVerified,
    handleResendOTP,
    handleVerifyChallengeAttribute,
    otpError,
  };
};

export default usePaymentOtp;
